/**
 * Tauri event subscription with a teardown that cannot orphan its callback.
 *
 * ## The defect this exists to close
 *
 * `listen()` from `@tauri-apps/api` returns an unlisten that does, in order:
 *
 * ```ts
 * window.__TAURI_EVENT_PLUGIN_INTERNALS__.unregisterListener(event, eventId); // sync
 * await invoke('plugin:event|unlisten', { event, eventId });                  // async
 * ```
 *
 * The first step evals Tauri's `unlisten_js_script`, which calls
 * `unregisterCallback(listeners[eventId].handlerId)` and — this is the whole
 * problem — leaves `listeners[eventId]` itself in place. Rust's delivery
 * script guards on that entry, not on the callback:
 *
 * ```js
 * const listener = listeners[id];
 * if (listener) { window.__TAURI_INTERNALS__.runCallback(listener.handlerId, eventData); }
 * ```
 *
 * So between the two steps the guard still passes while the callback is
 * already gone, and every event emitted in that window logs
 * `[TAURI] Couldn't find callback id <n>`. For a ~30 FPS stream broadcast to
 * every webview that is one warning per frame for the length of an IPC
 * round trip. If the second step never lands, the Rust listener is never
 * removed and the warning repeats at the event's native rate for as long as
 * the document lives.
 *
 * ## What this module does instead
 *
 * It registers the callback itself, so it holds both the `handlerId` and the
 * `eventId`, and on release it inverts Tauri's order:
 *
 * 1. point the handler reference at nothing — the callback stays registered,
 *    so an event arriving mid-teardown resolves into a no-op rather than a
 *    warning;
 * 2. await `plugin:event|unlisten`, so the Rust side stops emitting to us
 *    FIRST, with a bounded retry because a dropped unlisten is what turns a
 *    few milliseconds of noise into a leak that outlives the subscription;
 * 3. only then unregister the callback.
 *
 * The cost of step 2 preceding step 3 is that a handler can be invoked once
 * more after its consumer believes it released. The tombstone makes that a
 * no-op, but a migrated handler must still tolerate it.
 *
 * ## What happens when the unlisten never lands
 *
 * The tombstone is a bridge across the teardown, NOT a permanent parking spot.
 * If the retry budget is spent the callback is unregistered anyway, and a later
 * call to the same release function may attempt the removal again — see the
 * release closure at the bottom of `managedListen` for why both halves of that
 * are load-bearing.
 *
 * Step 3 calls `unregisterCallback` directly rather than the plugin's
 * `unregisterListener`. Read at tauri 2.12.3, the script that one evals does
 * `unregisterCallback(listeners[eventId].handlerId)` (2.12 added a guard for a
 * missing entry) and nothing else, so for
 * a callback we minted the two are the same call — and the `listeners[eventId]`
 * entry is left behind either way, by upstream as much as by us.
 *
 * There is a two-line fix for this upstream — have `unlisten_js_script` delete
 * the listener entry so the guard does its job. This module does not depend on
 * it landing.
 *
 * Both of those readings are pinned by `VERIFIED_TAURI_VERSIONS` below and
 * enforced by `tauri-version-pin.test.ts`, so a bump past the versions they
 * were read at fails CI instead of passing silently.
 */

import { invoke, transformCallback } from '@tauri-apps/api/core';
import {
  type EventCallback,
  type EventName,
  type EventTarget,
  type Options,
  type Event as TauriEvent,
} from '@tauri-apps/api/event';
import { pluginCoreLogger } from '../config';
import { forgetCallbackLabel, recordCallbackLabel } from './callback-labels';

/**
 * Unlike Tauri's `UnlistenFn`, this resolves only once the Rust listener is
 * actually gone. Awaiting it is what lets a caller sequence teardown against
 * a restart; ignoring the promise is safe but gives up that ordering.
 *
 * IT NEVER REJECTS. Every failure is logged and degraded inside
 * `attemptTeardown`, which is what makes the bare `void release()` at every
 * call site correct BY CONTRACT rather than by inspection of this file. Do not
 * add a throwing path here without giving all of them a `.catch()` first: an
 * escaping rejection from a teardown, on a fix whose whole thesis is that a
 * dropped teardown failure is what costs you, would be the same bug wearing a
 * different hat.
 */
export type ManagedUnlistenFn = () => Promise<void>;

/**
 * Backoff before each retry of a rejected `plugin:event|unlisten`, and by its
 * length the attempt budget: 1 initial attempt + one per entry = 4, with
 * 1,000 ms of total delay. Both bounds are deliberate. An unretried failure
 * is what turns a millisecond burst into a leak that lasts hours; an
 * unbounded retry is the endless wakeup loop that the reference-counted
 * audio-levels subscription exists to avoid, which would be a regression
 * wearing a fix's clothes.
 */
const UNLISTEN_RETRY_BACKOFF_MS = [50, 200, 750];

/**
 * How many times the release function will run the budget above before it stops
 * trying. The inner budget covers a transient denial; this outer one exists
 * because a caller that still holds the release function may legitimately call
 * it again later, and a teardown that gave up must not be memoized into a
 * permanent no-op. Two, not unbounded: a release function invoked from a render
 * loop must not be able to spin `plugin:event|unlisten` forever, so one
 * subscription costs at most 8 invokes and 2,000 ms of backoff for its whole
 * lifetime.
 *
 * Which callers reach it, as of this PR: `audio-levels.ts` does — it retains
 * the handle past `teardown()` and spends the second attempt on the next claim,
 * which is why a transient denial there recovers on the next consumer mount
 * instead of leaking the Rust listener for the document's life. The four
 * `useDesktopIdleStream.ts` sites do NOT — each nulls its ref before invoking,
 * so `release` is unreferenced the moment it is first called and their
 * reachable behaviour is one pass of the inner budget, then reclaim and
 * degrade. The outer budget is the contract `managedListen` owes a caller that
 * keeps its handle, not a claim that every caller does.
 */
const MAX_RELEASE_ATTEMPTS = 2;

/**
 * The two versions this module was read against, as a TRIPWIRE rather than a
 * note: `tauri-version-pin.test.ts` compares each against what is actually
 * installed and fails the build on a bump.
 *
 * A comment alone would not do the job. The registration half is an equivalence
 * with `@tauri-apps/api`'s `listen()`, and the teardown half is pinned to the
 * body of the `tauri` crate's `unlisten_js_script` — both read once, by hand,
 * and neither is anything CI would otherwise notice changing. The specific
 * change to watch for is the upstream fix described in the module header: if
 * `unlisten_js_script` starts deleting `listeners[eventId]`, calling
 * `unregisterListener` in step 3 would begin to earn us that fix, and calling
 * `unregisterCallback` — which is what we do, because we minted the handler and
 * do not need the lookup — would quietly stop being equivalent to it.
 *
 * Minor granularity, so a patch bump does not become a chore. When this fails:
 * re-read `listen()` in `@tauri-apps/api/event` and `unlisten_js_script` in the
 * `tauri` crate, confirm the module header still describes them, then move the
 * version here.
 */
export const VERIFIED_TAURI_VERSIONS = {
  /** `@tauri-apps/api` — the `listen()` equivalence in the module header. */
  jsApi: '2.12',
  /** the `tauri` crate — `unlisten_js_script`, which step 3 is pinned to. */
  rustCrate: '2.12',
} as const;

/**
 * The single member of `window.__TAURI_INTERNALS__` this module reaches for.
 *
 * DECLARED LOCALLY, not through a global `Window` augmentation, and that is a
 * consequence of the move rather than a style choice. The app's
 * augmentation lives in the root `custom.d.ts`, which is outside this package's
 * `include: ["src"]`, so the package typecheck cannot see it. Re-declaring it
 * globally here would put two `interface Window` augmentations for the same
 * property in the ROOT tsc program (which does read `packages/**`), and they
 * would have to stay byte-compatible forever. A local structural type over one
 * property cannot collide with anything.
 */
type TauriInternals = {
  unregisterCallback?: (id: number) => void;
};

function internals(): TauriInternals | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { __TAURI_INTERNALS__?: TauriInternals })
    .__TAURI_INTERNALS__;
}

function unregisterCallback(handlerId: number): void {
  internals()?.unregisterCallback?.(handlerId);
}

function resolveTarget(target: Options['target']): EventTarget {
  if (typeof target === 'string') return { kind: 'AnyLabel', label: target };
  return target ?? { kind: 'Any' };
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Remove the Rust-side listener, retrying within the backoff budget above.
 * Returns whether it is gone. A `false` means Rust may still emit to this
 * handler id — see the release closure for what is done about that, which is
 * deliberately NOT "keep the callback registered forever".
 */
async function removeRustListener(event: EventName, eventId: number): Promise<boolean> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      await invoke('plugin:event|unlisten', { event, eventId });
      return true;
    } catch (error) {
      const backoffMs = UNLISTEN_RETRY_BACKOFF_MS[attempt];
      if (backoffMs === undefined) {
        pluginCoreLogger().warn(
          '[ManagedListen] Failed to remove Rust listener; reclaiming the callback anyway',
          {
            event,
            attempts: attempt + 1,
            error: error instanceof Error ? error.message : String(error),
          }
        );
        return false;
      }
      await delay(backoffMs);
    }
  }
}

/**
 * Subscribe to a Tauri event. Drop-in for `listen()` except that the returned
 * release function is async: it resolves once the Rust listener is gone.
 *
 * The handler must tolerate being called one extra time after release — see
 * the module header.
 */
export async function managedListen<T>(
  event: EventName,
  handler: EventCallback<T>,
  options?: Options
): Promise<ManagedUnlistenFn> {
  // The registered callback is a STABLE dispatcher over a mutable reference.
  // Releasing nulls the reference, which is what makes the tombstone possible
  // without swapping an entry inside Tauri's private `callbacks` Map — a
  // structure that is exposed for debugging and is free to move between
  // versions.
  let liveHandler: EventCallback<T> | null = handler;
  const handlerId = transformCallback<TauriEvent<T>>((payload) => {
    liveHandler?.(payload);
  });
  recordCallbackLabel(handlerId, { kind: 'listen', name: event });

  let eventId: number;
  try {
    eventId = await invoke<number>('plugin:event|listen', {
      event,
      target: resolveTarget(options?.target),
      handler: handlerId,
    });
  } catch (error) {
    // Nothing on the Rust side to remove — the command never succeeded — so
    // the callback can go immediately rather than leaking for the session.
    liveHandler = null;
    forgetCallbackLabel(handlerId);
    unregisterCallback(handlerId);
    throw error;
  }

  let teardown: Promise<void> | null = null;
  let attemptsSpent = 0;
  let callbackReclaimed = false;

  const reclaimCallback = (): void => {
    if (callbackReclaimed) return;
    callbackReclaimed = true;
    unregisterCallback(handlerId);
  };

  const attemptTeardown = async (): Promise<void> => {
    liveHandler = null;
    let removed = false;

    // THE TWO `catch`ES BELOW ARE WHAT MAKE `ManagedUnlistenFn` TOTAL, so do
    // not collapse them into the one line you can currently see throwing.
    // `removeRustListener` swallows its own invoke errors, but everything in
    // this function reaches into ambient globals the module does not own —
    // `window.__TAURI_INTERNALS__.unregisterCallback`, and the logger on
    // `removeRustListener`'s give-up path. Every call site is a bare
    // `void release()` with no `.catch()`, so an escape here is an
    // `unhandledrejection` raised BY a teardown, on a fix whose whole thesis is
    // that a dropped teardown failure is what costs you.
    //
    // Neither is logged: the logger is one of the two globals that can put us
    // here, so reporting through it is how a total promise becomes a rejecting
    // one again.
    try {
      removed = await removeRustListener(event, eventId);
    } catch {
      // An unknown outcome is treated as a failure, which is the conservative
      // direction: the caller keeps its retry rather than memoizing a teardown
      // that may never have happened.
      removed = false;
    }

    try {
      // RECLAIM THE CALLBACK EVEN WHEN THE UNLISTEN NEVER LANDED. Do not read
      // this as a bug and "fix" it back into an early return, and do not move
      // it inside the block above — it has to run whichever half failed.
      //
      // Keeping the tombstone alive on exhaustion buys silence at the price of
      // a permanently-live listener nothing can ever collect, with every later
      // subscription stacking another one behind it — and stacking INVISIBLY,
      // because the tombstone absorbs the warning that would have named it.
      // Unregistering degrades this ONE listener to the pre-fix behaviour
      // instead: Rust still emits, so `[TAURI] Couldn't find callback id <n>`
      // comes back — but it is visible, it names the event (the label is kept
      // below on purpose so that it can), and it does not accumulate. Loud and
      // diagnosable beats silent and stacking.
      reclaimCallback();

      if (removed) {
        // Only on SUCCESS. `forgetCallbackLabel`'s own docstring is the ruling
        // here: "an id whose teardown did NOT complete keeps its label on
        // purpose: that is exactly the id a warning would name." Do not lift
        // this call out of the branch to "tidy" it — dropping the label on the
        // failure path is what would make the returning warning anonymous
        // again, which is precisely the state the labelling work exists to end.
        forgetCallbackLabel(handlerId);
      }
    } catch {
      // Same contract as above. `reclaimCallback` has already flipped its own
      // guard by the time it can throw, so a later attempt will not re-enter a
      // global that is failing.
    }

    // DO NOT MEMOIZE A FAILED TEARDOWN. Clearing `teardown` is what lets a
    // caller that still holds this function attempt the removal again later;
    // the Rust listener is the resource that outlived us, and a second call is
    // the only chance left to reclaim it. Bounded by `MAX_RELEASE_ATTEMPTS`, so
    // a release() in a render loop cannot spin the IPC.
    if (!removed && attemptsSpent < MAX_RELEASE_ATTEMPTS) teardown = null;
  };

  return () => {
    // A non-null `teardown` is either in flight, the memoized SUCCESS, or the
    // last failure once the attempt budget is spent. The first two are reasons
    // to hand back the same promise; the third is the bound.
    if (teardown) return teardown;
    attemptsSpent += 1;
    teardown = attemptTeardown();
    return teardown;
  };
}
